import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '../context/AuthContext';
import './UserRole.css';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import axios from 'axios';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import MenuItems from '../menubar/MenuItems';
import MobileMenuBar from '../menubar/MobileMenuBar';
import useMediaQuery from "@mui/material/useMediaQuery";
import UserDots from '../menubar/UserDots';
import { getBulkUploadUrl, getAllUsersUrl, getFirstTimePwdReset, getDeleteUserUrl } from '../constants';
import { FaDownload } from 'react-icons/fa'; // Import FontAwesome download icon
import { FaFileExcel } from "react-icons/fa";
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { confirmStatusUpdate, confirmPwdReset, showValidationAlert } from '../CustomBannerAlert'; // Import the showValidationAlert function
import Logo from '../menubar/Logo'
import UserEditModal from "./UserEditModal"; // Import the modal
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { styled } from "@mui/material/styles";
import Paper from "@mui/material/Paper";
import { Box, Grid } from "@mui/material";
import { FaBars, FaTimes, FaChevronLeft } from "react-icons/fa"; // Icons for open/close

const Item = styled(Paper)(({ theme }) => ({
    backgroundColor: "transparent",
    ...theme.typography.body2,
    padding: theme.spacing(1),
    color: theme.palette.text.secondary,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "none",
}));


function UserRole() {
    const { token, logout } = useContext(AuthContext);
    const [searchQuery, setSearchQuery] = useState("");
    const [data, setData] = useState([]);
    const [moduleHeaders, setModuleHeaders] = useState([]);
    const [expandedRows, setExpandedRows] = useState(null);
    const [loading, setLoading] = useState(false); // Spinner state
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState(null);
    const navigate = useNavigate();
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [showMenu, setShowMenu] = useState(true);
    const isMobile = useMediaQuery("(max-width:768px)");

    const toggleMenu = () => {
        setShowMenu(!showMenu);
    };

    const fetchUsers = useCallback(() => {
        if (!token) return; // Prevent API call if token is missing

        setLoading(true);
        axios
            .get(getAllUsersUrl(), {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            })
            .then(response => {
                setData(response.data);
                setModuleHeaders(Object.keys(response.data[0]?.moduleAccess || {}));
            })
            .catch(error => {
                console.error("Error fetching users:", error);
                showValidationAlert(`Error fetching users`, "error");
                if (error.response?.status === 403) {
                    logout();
                    navigate('/');
                }
            })
            .finally(() => setLoading(false));
    }, [token, logout, navigate]);



    useEffect(() => {
        if (token) {
            fetchUsers();
            updateRowsPerPage();
            window.addEventListener("resize", updateRowsPerPage);
            return () => window.removeEventListener("resize", updateRowsPerPage);
        }
    }, [token, fetchUsers]);

    const updateRowsPerPage = () => {
        const screenHeight = window.innerHeight;
        if (screenHeight >= 1440) {   // Large screens
            setRowsPerPage(20);
        } else if (screenHeight >= 960) {  // Medium screens
            setRowsPerPage(10);
        } else {   // Small screens
            setRowsPerPage(5);
        }
    };

    const handleEdit = (rowData) => {
        setSelectedUserId(rowData.empNo);
        setEditModalVisible(true);
    };

    const handleResetPassword = async (rowData) => {
        try {
            const response = await axios.post(getFirstTimePwdReset(),
                {   // Request Body (Data Payload)
                    empid: rowData.empNo,
                    password: rowData.empNo,
                    security_question: "reset",
                    security_ans: "reset",
                },
                {   // Headers
                    headers: {
                        Authorization: `Bearer ${token}`, // Pass token here
                        "Content-Type": "application/json",
                    },
                }
            );

            if (response.status === 201) {
                showValidationAlert(`Password reset is successful for: ${rowData.empName}`, "success");
            } else {
                showValidationAlert(`Something went wrong. Please try again. ${rowData.empName}`, "error");
            }
        } catch (error) {
            console.error("Error:", error);
            showValidationAlert("Failed to connect to the server. Please try again later.", "error");
            if (error.response && error.response.status === 403) {
                logout();
                navigate('/');
            }
        }
    };

    const handleToggleStatus = async (empNo, newStatus) => {
        if (!empNo) {
            console.error("empNo is undefined. Cannot update status.");
            return;
        }

        const payload = {
            empNo: empNo,
            empStatus: newStatus,
        };


        try {
            console.log("Sending request:", payload);
            const response = await axios.put(getDeleteUserUrl(), payload, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });

            console.log("API Response:", response.data);
            if (response.status === 200) {
                showValidationAlert(response.data.message || "Status updated successfully", "success");
                fetchUsers(); // Refresh user list
            } else {
                showValidationAlert("Failed to update status. Please try again.", "error");
            }
        } catch (error) {
            console.error("Error toggling status:", error);
            showValidationAlert("Error updating status. Please try again.", "error");
            if (error.response && error.response.status === 403) {
                logout();
                navigate('/');
            }
        }
    };






    const onSaveReloadUser = () => {
        showValidationAlert(`Users saved successfully`, "success");
        fetchUsers();
    }
    // Handle Excel Upload
    const handleExcelUpload = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.readAsArrayBuffer(file);
        setLoading(true); // Start spinner
        reader.onload = async (e) => {
            const workbook = new ExcelJS.Workbook();
            await workbook.xlsx.load(e.target.result);
            const worksheet = workbook.worksheets[0];
            const newData = [];
            const headers = worksheet.getRow(1).values.slice(1);

            setModuleHeaders(headers.slice(6));

            worksheet.eachRow((row, rowNumber) => {
                if (rowNumber > 1) {
                    let user = {
                        empNo: row.getCell(1).value,
                        empName: row.getCell(2).value,
                        dept: row.getCell(3).value,
                        storeNo: row.getCell(4).value,
                        storeName: row.getCell(5).value,
                        country: row.getCell(6).value,
                        empStatus: 1,
                        moduleAccess: {},
                    };

                    headers.slice(6).forEach((mod, index) => {
                        user.moduleAccess[mod] = row.getCell(index + 7).value?.toLowerCase() === "yes";
                    });

                    newData.push(user);
                }
            });

            setData(newData);

            axios.post(getBulkUploadUrl(), newData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            })
                .then(() => {
                    console.log("Users saved successfully");
                    showValidationAlert(`Users saved successfully`, "success");
                })
                .catch(error => {
                    console.error("Error saving users:", error);
                    showValidationAlert(`Error saving users`, "error");
                    if (error.response && error.response.status === 403) {
                        logout();
                        navigate('/');
                    }
                })
                .finally(() => setLoading(false)); // Stop spinner
        };
    };


    // Export to Excel
    const handleExportExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("Users");

        worksheet.columns = [
            { header: "EmployeeNo", key: "empNo", width: 30 },  // Increased width
            { header: "EmployeeName", key: "empName", width: 35 }, // Increased width for longer names
            { header: "Department", key: "dept", width: 25 },  // Slightly wider
            { header: "StoreNo", key: "storeNo", width: 20 },  // Increased width
            { header: "StoreName", key: "storeName", width: 30 },  // Wider for longer names
            { header: "Country", key: "country", width: 20 },  // Adjusted for consistency
            ...moduleHeaders.map(mod => ({ header: mod, key: mod, width: 20 })) // Adjusted dynamic columns
        ];


        worksheet.addRows(data.map(user => ({
            ...user,
            ...user.moduleAccess,
        })));

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        saveAs(blob, "EmployeeData.xlsx");
    };

    const actionBodyTemplate = (rowData) => (
        <div className="action-buttons" >
            {/* <Button icon="pi pi-copy" className="p-button-rounded p-button-text" onClick={() => handleCopy(rowData)} title="Copy" /> */}
            <Button icon="pi pi-pencil" className="p-button-rounded p-button-text p-button-warning" onClick={() => handleEdit(rowData)} title="Edit" />
            <Button icon="pi pi-refresh" className="p-button-rounded p-button-text p-button-info" onClick={() => confirmPwdReset(rowData, handleResetPassword)} title="Reset Password" />

            {/* Status Toggle Checkbox */}
            <input
                type="checkbox"
                className="check-box-access"
                checked={rowData.empStatus === 1}
                onChange={() => confirmStatusUpdate(rowData, rowData.empStatus === 1 ? 0 : 1, handleToggleStatus)}
                title={rowData.empStatus === 1 ? "Deactivate" : "Activate"}
            />

        </div>
    );

    const rowExpansionTemplate = (rowData) => (
        <table className="expanded-table">

            <tbody>
                {moduleHeaders.map(mod => (
                    <tr key={mod}>
                        <td>{mod}</td>
                        <td className={rowData.moduleAccess[mod] ? "access-yes" : "access-no"}>
                            {rowData.moduleAccess[mod] ? "✅ Access" : "❌ No Access"}
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
    const filteredData = data.filter((user) =>
        Object.values(user)
            .join(" ")
            .toLowerCase()
            .includes(searchQuery.toLowerCase())
    );

    const formatStatusDate = (value) => {
        if (!value) return ''; // Handle null or empty cases
        const date = new Date(value);
        return format(date, "yyyy-MM-dd");
    };

    return (

        <Box className="user-role-container">
            {loading && <div className="loading-overlay"><span className="spinner"></span></div>}

            {/* Menu Toggle Button */}
            <button className="menu-toggle-btn" onClick={toggleMenu}>
                {showMenu ? <FaChevronLeft /> : <FaBars />}
            </button>

            <Grid container spacing={2}>


                {isMobile ? (
                    <div style={{ width: '100%', display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
                        <MobileMenuBar />
                    </div>
                ) : (
                    <>
                        <Grid item xs={12}>
                            <Logo />
                            <UserDots />
                        </Grid>
                        {/* Left Menu - Collapsible */}
                        {showMenu && (
                            <Grid item xs={12} sm={3} md={2.3} lg={2.4} className="user-role-menu">
                                <Item style={{ paddingLeft: '10px' }}>
                                    <MenuItems />
                                </Item>
                            </Grid>
                        )}
                    </>
                )}


                {/* Main Content - Adjust Width */}
                <Grid item xs={12} sm={showMenu ? 9 : 12} md={showMenu ? 9.7 : 12} lg={showMenu ? 9.6 : 12}>
                    <Item style={{ width: "90%" }} className="user-role-search">
                        <div className="p-inputgroup">
                            <div className="search-container">
                                <InputText
                                    placeholder="Search..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="search-input"
                                />
                                {searchQuery && (
                                    <Button className="clear-btn" style={{ backgroundColor: "#dc3545" }}
                                        onClick={() => setSearchQuery("")}>
                                        <FaTimes size={16} />
                                    </Button>
                                )}
                            </div>

                            {!isMobile && (
                            <div className="right-buttons">
                                <label className="custom-file-input">
                                    <input type="file" accept=".xlsx" onChange={handleExcelUpload} />
                                    <FaFileExcel className="excel-icon" />
                                    <span>Upload Excel</span>
                                </label>

                                <Button className="export-btn download-btn" onClick={handleExportExcel}>
                                    <FaDownload className="download-icon" />&nbsp;Download Excel
                                </Button>
                            </div>
                        )}
                        </div>
                    </Item>

                    <Item style={{ width: "100%" }} className="user-role-table">
                        <DataTable
                            style={{
                                width: "100%",
                                minHeight: "500px", // Set a minimum height
                                maxHeight: "calc(100vh - 150px)", // Ensures it doesn't exceed viewport
                                overflowY: "auto", // Allows scrolling when needed
                            }}
                            value={filteredData}
                            paginator
                            rows={rowsPerPage}
                            rowsPerPageOptions={[5, 10, 20, 30, 50]}
                            scrollable

                            scrollHeight="calc(100vh - 150px)"
                            expandedRows={expandedRows}
                            onRowToggle={(e) => setExpandedRows(e.data)}
                            rowExpansionTemplate={rowExpansionTemplate}
                            footer={
                                <div style={{ textAlign: 'right', paddingRight: '6px', fontWeight: 'bold', fontFamily: 'Calibri' }}>
                                    <span style={{ fontFamily: 'Calibri' }}>Total Records: {filteredData.length}</span>
                                </div>
                            }
                        >
                            <Column expander style={{ width: '10px' }} />
                            <Column field="country" header="Country" />
                            <Column field="storeNo" header="StoreNo" />
                            <Column field="storeName" header="StoreName" style={{ minWidth: '150px' }} />
                            <Column field="dept" header="Dept" sortable />
                            <Column field="empNo" header="EmpNo" sortable />
                            <Column field="empName" header="EmpName" />
                            <Column field="empStausDt" header="StatusDate" sortable body={(rowData) => formatStatusDate(rowData.empStausDt)} />
                            <Column
                                field="empStatus"
                                header="Status"
                                sortable
                                body={(rowData) => (
                                    <span className={rowData.empStatus === 1 ? "status-active" : "status-inactive"}>
                                        {rowData.empStatus === 1 ? "Active" : "Inactive"}
                                    </span>
                                )}
                                style={{ textAlign: 'center' }}
                            />
                            <Column
                                header="Edit"
                                body={(rowData) => (
                                    <Button
                                        icon="pi pi-pencil"
                                        className="p-button-rounded p-button-text p-button-warning"
                                        onClick={() => handleEdit(rowData)}
                                        title="Edit"
                                    />
                                )}
                                style={{ textAlign: 'center' }}
                            />
                            <Column
                                header="Reset"
                                body={(rowData) => (
                                    <Button
                                        icon="pi pi-refresh"
                                        className="p-button-rounded p-button-text p-button-info"
                                        onClick={() => confirmPwdReset(rowData, handleResetPassword)}
                                        title="Reset Password"
                                    />
                                )}
                                style={{ textAlign: 'center' }}
                            />
                            <Column
                                header="Action"
                                body={(rowData) => (
                                    <input
                                        type="checkbox"
                                        className="check-box-access"
                                        checked={rowData.empStatus === 1}
                                        onChange={() => confirmStatusUpdate(rowData, rowData.empStatus === 1 ? 0 : 1, handleToggleStatus)}
                                        title={rowData.empStatus === 1 ? "Deactivate" : "Activate"}
                                    />
                                )}
                                style={{ textAlign: 'center' }}
                            />
                        </DataTable>

                        {/* Edit Modal */}
                        <UserEditModal
                            visible={editModalVisible}
                            onHide={() => setEditModalVisible(false)}
                            userId={selectedUserId}
                            onSave={onSaveReloadUser}
                        />
                        <ToastContainer />
                    </Item>
                </Grid>
            </Grid>
        </Box>
    );
}

export default UserRole;
